import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import assistantService from '@services/assistant';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';

export const LATEST_ASSISTANT_CONVERSATION_FOR_PROJECT_QUERY_KEY = 'myLatestAssistantConversationForProject';

/**
 * Sprint 37, story-199. Resolves where a project's assistant button should go. Runs the
 * latest-for-project query ON CLICK (never on page load, so project pages incur no extra
 * request): a resumable conversation -> `/assistant/<id>`; otherwise, or when the query fails,
 * an empty project-bound chat at `/assistant?project=<id>`.
 */
export const useResumeProjectConversation = () => {
  const queryClient = useQueryClient();
  const { organizationId } = useAppSelector((state: RootState) => state.organization);
  const [isResolving, setIsResolving] = useState(false);

  const resolveRoute = useCallback(
    async (projectId: string): Promise<string> => {
      setIsResolving(true);
      try {
        const latest = await queryClient.fetchQuery({
          queryKey: [LATEST_ASSISTANT_CONVERSATION_FOR_PROJECT_QUERY_KEY, organizationId, projectId],
          queryFn: () => assistantService.getLatestAssistantConversationForProject(projectId),
          staleTime: 0,
        });
        return latest ? `/assistant/${latest.conversationId}` : `/assistant?project=${encodeURIComponent(projectId)}`;
      } catch {
        return `/assistant?project=${encodeURIComponent(projectId)}`;
      } finally {
        setIsResolving(false);
      }
    },
    [queryClient, organizationId],
  );

  return { resolveRoute, isResolving };
};

export default useResumeProjectConversation;
